/**
 * OpenStreetMap Overpass API client.
 *
 * Generic Overpass query helper + an Overpass-JSON to GeoJSON converter,
 * plus a roads-specific composite helper used by the Major Roads overlay.
 *
 * Endpoints:
 * - Primary:   https://overpass.kumi.systems/api/interpreter
 *              (CORS verified: Access-Control-Allow-Origin: *)
 * - Fallback:  https://overpass-api.de/api/interpreter
 *              (curl returns 406 to non-standard UAs; widely reported to work in browsers)
 *
 * Public Overpass endpoints rate-limit per-IP (not per-user). Callers must
 * debounce viewport-driven fetches and gate on a sane MIN_ZOOM. See the
 * 2026-05-15-sign-table-planning-design.md doc for the empirical payload
 * measurements that informed the MIN_ZOOM=13 floor used by the overlay.
 */

const OVERPASS_ENDPOINTS = [
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass-api.de/api/interpreter',
] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OverpassWay {
  type: 'way';
  id: number;
  nodes: number[];
  geometry?: Array<{ lat: number; lon: number }>;
  tags?: Record<string, string>;
}

type OverpassElement = OverpassWay | { type: 'node' | 'relation'; id: number };

export interface OverpassResponse {
  version: number;
  generator: string;
  elements: OverpassElement[];
}

/** GeoJSON properties attached to road LineStrings. */
export interface RoadFeatureProperties {
  highway: string;
  ref?: string;
  name?: string;
  /** True when the way has a `ref=*` tag (state/US route — high-confidence busy road). */
  hasRef: boolean;
}

// ---------------------------------------------------------------------------
// Pure functions (unit-tested)
// ---------------------------------------------------------------------------

/**
 * Convert an Overpass JSON response into a GeoJSON LineString FeatureCollection.
 *
 * Only `type=way` elements with a non-empty geometry array of at least two points
 * are included. The way's `id` becomes the feature `id`. All tags are passed
 * through to feature `properties`, with a derived `hasRef` boolean for styling.
 */
export function overpassToGeoJSON(
  response: OverpassResponse,
): GeoJSON.FeatureCollection<GeoJSON.LineString, RoadFeatureProperties> {
  const features: GeoJSON.Feature<GeoJSON.LineString, RoadFeatureProperties>[] = [];

  for (const el of response.elements) {
    if (el.type !== 'way') continue;
    const way = el as OverpassWay;
    if (!way.geometry || way.geometry.length < 2) continue;
    const tags = way.tags ?? {};
    if (!tags.highway) continue;

    features.push({
      type: 'Feature',
      id: way.id,
      geometry: {
        type: 'LineString',
        coordinates: way.geometry.map((p) => [p.lon, p.lat]),
      },
      properties: {
        ...tags,
        highway: tags.highway,
        // OSM bulk imports occasionally include `ref=""` — treat as no-ref.
        hasRef: typeof tags.ref === 'string' && tags.ref.length > 0,
      } as RoadFeatureProperties,
    });
  }

  return { type: 'FeatureCollection', features };
}

interface BuildRoadsQueryOptions {
  /**
   * Regional-only mode: omit the broad `highway=primary|secondary|tertiary`
   * selector and return only ways with `ref=*` (state/US/county routes).
   * Sparse — typically a few hundred features per metro vs thousands for
   * the full composite. Use at low zoom levels (z11–z12) to give viewport
   * context without paying full-composite payload costs.
   */
  regionalOnly?: boolean;
}

/**
 * Build the Overpass QL query for the Major Roads overlay.
 *
 * Default mode: union of (highway primary|secondary|tertiary) and
 * (any way with both highway=* and ref=*). The `ref` selector captures
 * state/US-numbered routes that may be inconsistently tagged at the
 * highway=* level — those are the most reliable busy-road signal.
 *
 * Regional-only mode (zoom < 13): just the `ref` selector, much smaller
 * payload, gives state/US route context without the full neighborhood detail.
 *
 * Bounds are accepted as [west, south, east, north] (the project's standard
 * GeoJSON-style ordering). Overpass uses (south,west,north,east), so we
 * reorder here.
 */
export function buildRoadsQuery(
  bounds: [west: number, south: number, east: number, north: number],
  options: BuildRoadsQueryOptions = {},
): string {
  const [west, south, east, north] = bounds;
  const bbox = `${south},${west},${north},${east}`;
  if (options.regionalOnly) {
    return (
      `[out:json][timeout:25];` +
      `way["ref"]["highway"](${bbox});` +
      `out geom;`
    );
  }
  return (
    `[out:json][timeout:25];` +
    `(` +
    `way["highway"~"^(primary|secondary|tertiary)$"](${bbox});` +
    `way["ref"]["highway"](${bbox});` +
    `);` +
    `out geom;`
  );
}

// ---------------------------------------------------------------------------
// Fetch wrappers
// ---------------------------------------------------------------------------

interface FetchOptions {
  /** Optional AbortSignal — propagates to the underlying fetch so callers can cancel in-flight requests. */
  signal?: AbortSignal;
}

/**
 * Submit an Overpass QL query and return the parsed JSON response.
 *
 * Tries the primary endpoint first; on network error or non-2xx, falls back
 * to the secondary. Throws if both fail — callers should catch and degrade
 * silently (the overlay drops to "no roads rendered" rather than failing
 * the host map). If the abort signal fires, the underlying fetch rejects
 * with AbortError, which we re-throw so the caller can distinguish
 * cancellation from real failure.
 */
export async function queryOverpass(
  query: string,
  options: FetchOptions = {},
): Promise<OverpassResponse> {
  let lastError: unknown;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(query)}`,
        signal: options.signal,
      });
      if (!res.ok) {
        lastError = new Error(`Overpass ${endpoint} failed: ${res.status}`);
        continue;
      }
      return (await res.json()) as OverpassResponse;
    } catch (err) {
      // Don't fall through on cancellation — the caller wants to know.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Overpass: all endpoints failed');
}

/**
 * Fetch road LineStrings for a viewport. Composed from buildRoadsQuery +
 * queryOverpass + overpassToGeoJSON. Zoom-gating + mode selection live in
 * the overlay component, not here — this is a pure data fetch.
 */
export async function fetchOsmRoads(
  bounds: [west: number, south: number, east: number, north: number],
  options: BuildRoadsQueryOptions & FetchOptions = {},
): Promise<GeoJSON.FeatureCollection<GeoJSON.LineString, RoadFeatureProperties>> {
  const query = buildRoadsQuery(bounds, { regionalOnly: options.regionalOnly });
  const response = await queryOverpass(query, { signal: options.signal });
  return overpassToGeoJSON(response);
}
